import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Printer, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { getReceipt } from "../services/payments";
import { fmtUSD } from "../lib/pricing";
import { Button } from "../components/ui/button";

// Simple, printable payment receipt for a paid case.
export default function Receipt() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getReceipt(sessionId).then(setData).catch(() => setData(false)).finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  if (!data) return <div className="py-24 text-center text-muted-foreground" data-testid="receipt-not-found">{t("receipt.notFound")}</div>;

  const Row = ({ label, value, strong }) => (
    <div className={`flex justify-between py-2 ${strong ? "border-t border-border font-semibold" : ""}`}>
      <span className={strong ? "" : "text-muted-foreground"}>{label}</span>
      <span data-testid={`receipt-${label}`}>{value}</span>
    </div>
  );

  return (
    <div className="mx-auto max-w-lg px-4 py-10" data-testid="receipt-page">
      <div className="mb-4 flex items-center justify-between print:hidden">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="receipt-back"><ArrowLeft className="mr-1.5 h-4 w-4" /> {t("common.back")}</Button>
        <Button size="sm" className="rounded-full bg-brand text-white hover:bg-brand/90" onClick={() => window.print()} data-testid="receipt-print"><Printer className="mr-1.5 h-4 w-4" /> {t("receipt.print")}</Button>
      </div>

      <div className="rounded-xl border border-border bg-card p-8 shadow-sm">
        <div className="flex items-center justify-between border-b border-border pb-4">
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight">DentalCAD Connect</h1>
            <p className="text-sm text-muted-foreground">{t("receipt.title")}</p>
          </div>
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${data.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            {data.payment_status === "paid" && <CheckCircle2 className="h-3.5 w-3.5" />}
            {t(`admin.paymentStatus.${data.payment_status}`, data.payment_status)}
          </span>
        </div>

        <div className="mt-4 space-y-0.5 text-sm">
          <Row label={t("receipt.caseId")} value={data.case_number || data.case_id || "—"} />
          <Row label={t("admin.designer")} value={data.designer_name || "—"} />
          {data.created_at && <Row label={t("receipt.date")} value={new Date(data.created_at).toLocaleString()} />}
        </div>

        <div className="mt-6 space-y-0.5 text-sm">
          {data.promo_applied ? (
            <>
              <Row label={t("promo.designerService")} value={fmtUSD(data.designer_net)} />
              <Row label={t("promo.regularFee", { rate: Math.round((data.original_commission_rate || 0.15) * 100) })} value={fmtUSD(data.original_commission_amount)} />
              <Row label={`${t("promo.launchDiscount")} (LAUNCH50)`} value={`- ${fmtUSD(data.discount_amount)}`} />
              <Row label={t("promo.finalFee")} value={fmtUSD(data.final_commission_amount)} />
              <Row label={t("receipt.totalPaid")} value={fmtUSD(data.amount)} strong />
            </>
          ) : (
            <>
              <Row label={t("receipt.serviceAmount")} value={fmtUSD(data.designer_net)} />
              <Row label={t("receipt.serviceFee")} value={fmtUSD(data.commission_amount)} />
              <Row label={t("receipt.totalPaid")} value={fmtUSD(data.amount)} strong />
            </>
          )}
        </div>

        <div className="mt-6 border-t border-border pt-4 text-xs text-muted-foreground">
          <p>{t("receipt.txnId")}: <span className="font-mono">{data.payment_intent || data.session_id}</span></p>
        </div>
      </div>
    </div>
  );
}
